import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Collapse,
  Divider,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Radio,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect } from "react";
import {
  HiOutlineCheckCircle,
  HiOutlineGlobeAlt,
  HiPlus,
} from "react-icons/hi2";

import { Button } from "../../../../components";
import { type MaterialInput, type MaterialInputMethod } from "./types";
import { useExtractTextFromUrl } from "./useExtractTextFromUrl";

export type { MaterialInput, MaterialInputMethod };

/* ---------- Options config ---------- */

type RadioOptionDef = {
  value: MaterialInputMethod;
  title: string;
  description: string;
  requiresAts?: boolean;
};

const INPUT_METHOD_OPTIONS: RadioOptionDef[] = [
  {
    value: "type_or_paste",
    title: "Type or paste text",
    description: "Copy and paste content directly",
  },
  {
    value: "upload",
    title: "Upload document",
    description: "Add a PDF, DOC, or other file",
  },
  {
    value: "add_link",
    title: "Add link",
    description: "Paste a publicly available URL to a document or file",
  },
  {
    value: "brighthire",
    title: "Select from BrightHire",
    description: "Choose materials saved in BrightHire",
  },
  {
    value: "ats",
    title: "Select from ATS",
    description: "Import position materials from your ATS",
    requiresAts: true,
  },
];

/* ---------- Component ---------- */

type MaterialInputMethodSelectorProps = {
  isAtsConnected?: boolean;
  value: MaterialInput;
  onChange: (state: MaterialInput) => void;
  label?: string;
  textareaMinHeight?: string;
};

export const MaterialInputMethodSelector: React.FC<
  MaterialInputMethodSelectorProps
> = ({
  isAtsConnected = false,
  value,
  onChange,
  label = "Choose how to add",
  textareaMinHeight = "120px",
}) => {
  const { method: selectedMethod, textValue, linkValue } = value;

  const visibleOptions = INPUT_METHOD_OPTIONS.filter(
    (o) => !o.requiresAts || isAtsConnected
  );

  const {
    extract,
    loading: extractLoading,
    error: extractError,
    result: extractResult,
    reset: resetExtract,
  } = useExtractTextFromUrl();

  const handleMethodSelect = useCallback(
    (nextMethod: MaterialInputMethod | null) => {
      if (nextMethod !== "add_link") {
        resetExtract();
      }
      onChange({ ...value, method: nextMethod });
    },
    [onChange, value, resetExtract]
  );

  const handleTextChange = useCallback(
    (nextText: string) => {
      onChange({ ...value, textValue: nextText });
    },
    [onChange, value]
  );

  const handleLinkChange = useCallback(
    (nextLink: string) => {
      resetExtract();
      onChange({ ...value, linkValue: nextLink, linkExtractedText: "" });
    },
    [onChange, value, resetExtract]
  );

  const handleExtract = useCallback(() => {
    const url = value.linkValue.trim();
    if (!url) return;
    extract(url);
  }, [extract, value.linkValue]);

  // Sync extracted text into parent state when result arrives
  useEffect(() => {
    if (
      extractResult?.extractedText &&
      value.linkExtractedText !== extractResult.extractedText
    ) {
      onChange({ ...value, linkExtractedText: extractResult.extractedText });
    }
    // Only trigger when extractResult changes, not on every value/onChange change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractResult]);

  return (
    <VStack align="flex-start" spacing="0" w="100%">
      <Text fontSize="sm" color="gray.600" lineHeight="20px" mb="3">
        {label}
      </Text>
      <VStack spacing="0" align="stretch" w="100%">
        {visibleOptions.map((option) => {
          const isSelected = selectedMethod === option.value;
          const isVisible = !selectedMethod || isSelected;

          return (
            <Collapse key={option.value} in={isVisible} animateOpacity>
              <Box pb="2">
                <Box
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  overflow="hidden"
                  transition="border-color 0.2s"
                >
                  <Flex
                    align="center"
                    gap="3"
                    px="4"
                    py="3"
                    cursor="pointer"
                    bg="white"
                    _hover={{ bg: isSelected ? "white" : "gray.50" }}
                    onClick={() =>
                      handleMethodSelect(
                        selectedMethod === option.value ? null : option.value
                      )
                    }
                  >
                    <Radio
                      value={option.value}
                      size="lg"
                      flexShrink={0}
                      pointerEvents="none"
                      isChecked={isSelected}
                      colorScheme="blue"
                    />
                    <Box>
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        color="gray.800"
                        lineHeight="20px"
                      >
                        {option.title}
                      </Text>
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        lineHeight="20px"
                        mt="0.5"
                      >
                        {option.description}
                      </Text>
                    </Box>
                  </Flex>

                  <Collapse in={isSelected} animateOpacity>
                    <Divider borderColor="gray.200" />
                    <Box px="4" py="3">
                      {option.value === "type_or_paste" && (
                        <TypeOrPastePanel
                          value={textValue}
                          onChange={handleTextChange}
                          minHeight={textareaMinHeight}
                        />
                      )}
                      {option.value === "upload" && <UploadPanel />}
                      {option.value === "add_link" && (
                        <AddLinkPanel
                          value={linkValue}
                          onChange={handleLinkChange}
                          onExtract={handleExtract}
                          extractLoading={extractLoading}
                          extractError={extractError}
                          extractSuccess={!!extractResult}
                        />
                      )}
                      {option.value === "brighthire" && <BrightHirePanel />}
                      {option.value === "ats" && <AtsPanel />}
                    </Box>
                  </Collapse>
                </Box>
              </Box>
            </Collapse>
          );
        })}
      </VStack>
    </VStack>
  );
};

/* ---------- Content Panels ---------- */

type TextPanelProps = {
  value: string;
  onChange: (v: string) => void;
};

const TypeOrPastePanel: React.FC<TextPanelProps & { minHeight: string }> = ({
  value,
  onChange,
  minHeight,
}) => (
  <Textarea
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder="Paste or type content here..."
    fontSize="sm"
    minH={minHeight}
    resize="vertical"
    borderColor="gray.200"
    _placeholder={{ color: "gray.400" }}
  />
);

const UploadPanel: React.FC = () => (
  <VStack align="stretch" spacing="2">
    <Text fontSize="sm" color="gray.600" lineHeight="20px">
      Add document
    </Text>
    <Flex
      direction="column"
      align="center"
      justify="center"
      border="2px dashed"
      borderColor="gray.300"
      borderRadius="md"
      py="8"
      px="4"
      gap="2"
    >
      <Text
        fontSize="sm"
        fontWeight="medium"
        color="gray.700"
        textAlign="center"
      >
        Drag and drop files here or{" "}
        <Text as="span" color="blue.500" cursor="pointer" fontWeight="medium">
          browse files
        </Text>{" "}
        on your computer
      </Text>
      <Text fontSize="sm" color="gray.400" textAlign="center">
        Format: pdf, doc, rtf, txt &nbsp;&bull;&nbsp; Max size: 5MB
      </Text>
    </Flex>
  </VStack>
);

type AddLinkPanelProps = TextPanelProps & {
  onExtract: () => void;
  extractLoading: boolean;
  extractError: string | null;
  extractSuccess: boolean;
};

const AddLinkPanel: React.FC<AddLinkPanelProps> = ({
  value,
  onChange,
  onExtract,
  extractLoading,
  extractError,
  extractSuccess,
}) => (
  <VStack align="stretch" spacing="3">
    <Text fontSize="sm" color="gray.600" lineHeight="20px">
      Enter URL
    </Text>
    <InputGroup>
      <InputLeftElement pointerEvents="none">
        <Icon as={HiOutlineGlobeAlt} color="gray.400" />
      </InputLeftElement>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://www.example.com"
        fontSize="sm"
        borderColor="gray.200"
        _placeholder={{ color: "gray.400" }}
        isDisabled={extractLoading}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onExtract();
          }
        }}
      />
    </InputGroup>

    <Button
      variant="outline"
      size="sm"
      leftIcon={
        extractSuccess ? (
          <Icon as={HiOutlineCheckCircle} color="green.500" />
        ) : (
          <Icon as={HiPlus} />
        )
      }
      alignSelf="flex-start"
      borderColor={extractSuccess ? "green.200" : "gray.200"}
      color={extractSuccess ? "green.600" : "gray.700"}
      fontWeight="medium"
      onClick={onExtract}
      isDisabled={!value.trim() || extractLoading}
      isLoading={extractLoading}
      loadingText="Extracting..."
    >
      {extractSuccess ? "Content extracted" : "Add"}
    </Button>

    {extractError && (
      <Alert status="error" borderRadius="md" fontSize="sm">
        <AlertIcon />
        <AlertDescription>{extractError}</AlertDescription>
      </Alert>
    )}

    {extractSuccess && (
      <Alert status="success" borderRadius="md" fontSize="sm">
        <AlertIcon />
        <AlertDescription>
          Content extracted successfully and will be used as the job
          description.
        </AlertDescription>
      </Alert>
    )}
  </VStack>
);

const SAMPLE_BH_ITEMS = [
  { position: "Account Executive", author: "Content", date: "Sep 14, 2022" },
  {
    position: "Assistant to the Regional Manager",
    author: "Content",
    date: "Sep 14, 2022",
  },
  {
    position: "Customer Success Manager",
    author: "Content",
    date: "Sep 14, 2022",
  },
  { position: "Graphic Designer", author: "Content", date: "Sep 14, 2022" },
  {
    position: "Human Resources Specialist",
    author: "Content",
    date: "Sep 14, 2022",
  },
];

// TODO(ENG-19456): wire to real BrightHire plan data
const BrightHirePanel: React.FC = () => (
  <VStack align="stretch" spacing="2">
    <Text fontSize="sm" color="gray.600" lineHeight="20px">
      Select item to add
    </Text>
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      overflow="hidden"
    >
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th
              fontSize="xs"
              color="gray.500"
              fontWeight="medium"
              textTransform="none"
            >
              Position
            </Th>
            <Th
              fontSize="xs"
              color="gray.500"
              fontWeight="medium"
              textTransform="none"
            >
              Author
            </Th>
            <Th
              fontSize="xs"
              color="gray.500"
              fontWeight="medium"
              textTransform="none"
            >
              Last updated
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {SAMPLE_BH_ITEMS.map((item) => (
            <Tr key={item.position} _hover={{ bg: "gray.50" }} cursor="pointer">
              <Td fontSize="sm">{item.position}</Td>
              <Td fontSize="sm" color="gray.500">
                {item.author}
              </Td>
              <Td fontSize="sm" color="gray.500">
                {item.date}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  </VStack>
);

// TODO(ENG-19456): wire to real ATS position data
const AtsPanel: React.FC = () => (
  <VStack align="stretch" spacing="2">
    <Text fontSize="sm" color="gray.600" lineHeight="20px">
      Select option
    </Text>
    <Select placeholder="Select..." fontSize="sm" borderColor="gray.200" />
  </VStack>
);
